import { authRoles } from '../auth';


const navigationConfig = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'item',
        icon: 'dashboard',
        auth: authRoles.user,
        url: '/dashboard',
        exact: true
    },
    {
        id: 'workshop-dashboard',
        title: 'Dashboard',
        type: 'item',
        icon: 'dashboard',
        auth: authRoles.participant,
        training_allow: true,
        url: '/workshop-dashboard',
        exact: true
    },
    {
        id: 'compute',
        title: 'Compute / IDE',
        type: 'group',
        icon: 'apps',
        children: [
            {
                id: 'heidi-lab',
                title: 'Heidi Lab',
                type: 'collapse',
                icon: 'build',
                disabled: authRoles.demo,
                open: true,
                children: [
                    {
                        id: 'credential-manager',
                        title: 'Credentials Manager',
                        type: 'item',
                        icon: 'lock',
                        auth: authRoles.admin,
                        url: '/credentials',
                        exact: true
                    },
                    {
                        id: 'stack-manager',
                        title: 'Stack Manager',
                        type: 'item',
                        icon: 'album',
                        auth: authRoles.admin,
                        url: '/stacks',
                        exact: true
                    },
                    {
                        id: 'cluster-manager',
                        title: 'Cluster Manager',
                        training_allow: true,
                        type: 'item',
                        icon: 'dns',
                        url: '/clusters',
                        exact: true
                    }
                ],
            },
            {
                id: 'workloads',
                title: 'Workloads',
                type: 'collapse',
                icon: 'work',
                disabled: authRoles.demo,
                children: [
                    {
                        id: 'job-manager',
                        title: 'Job Manager',
                        type: 'item',
                        icon: 'work_outline',
                        url: '/jobs',
                        exact: true
                    },
                    {
                        id: 'file-manager',
                        title: 'File Manager',
                        type: 'item',
                        icon: 'folder',
                        url: '/files',
                        exact: true
                    }
                ]
            }
        ]
    },
    // Heidi Storage. Its own section, sitting next to Compute / IDE because
    // storage is a peer of compute, not a sub-tool of it.
    //
    // Nav labels are SHORT because the user is already inside Heidi - "Heidi
    // Storage > Heidi Documents" stutters. The full product names live in each
    // page's title and on every external surface, which is where they do their
    // work.
    {
        id: 'storage',
        title: 'Storage',
        type: 'group',
        icon: 'storage',
        children: [
            {
                id: 'storage-overview',
                title: 'Overview',
                type: 'item',
                icon: 'donut_large',
                url: '/storage',
                exact: true
            },
            {
                id: 'storage-fast',
                title: 'Fast Storage',
                type: 'item',
                icon: 'flash_on',
                url: '/storage/fast',
                exact: true
            },
            {
                id: 'storage-shared',
                title: 'Shared Storage',
                type: 'item',
                icon: 'storage',
                url: '/settings/storage/shared',
                exact: true
            },
        ]
    },
    // Vertical-agnostic, so it sits with Compute and Storage, above the
    // Education vertical. These are the apps a cluster runs (RStudio, Jupyter,
    // VS Code...), not course material - courses live under Education.
    {
        id: 'applications',
        title: 'Applications',
        type: 'group',
        icon: 'apps',
        children: [
            {
                id: 'apps',
                title: 'App Library',
                type: 'item',
                icon: 'apps',
                training_allow: true,
                url: '/heidi-apps',
                exact: true
            },
        ]
    },
    {
        // Our own section name, so Heidi's own AI components sit next to the
        // partner ones. Documents is always here; the watsonx items keep IBM's
        // product names (the subtitle says what each is for) and Navigation.js
        // removes them unless /api/watsonx/menu says the integration is on for
        // this installation AND the user's organization.
        id: 'ai-data',
        title: 'AI & Data',
        type: 'group',
        icon: 'device_hub',
        children: [
            {
                id: 'documents',
                title: 'Documents',
                type: 'item',
                icon: 'description',
                training_allow: true,
                url: '/storage/documents',
                exact: true
            },
            {
                // A viewer on the Storage store, like Documents above: DICOM
                // series and scientific image stacks. Navigation.js hides it
                // where the organization has no imaging.
                id: 'storage-imaging',
                title: 'Imaging',
                type: 'item',
                icon: 'photo_library',
                training_allow: true,
                url: '/storage/imaging',
                exact: true
            },
            // A peer of Documents here, not of Storage/Compute - fine-tuning a
            // model is an AI & Data task, not a storage one. Unattended,
            // multi-hour GPU jobs, so a workshop has to opt a participant into
            // it explicitly (Training.allowParticipantTraining, off by
            // default). `training_allow` only lifts the blanket participant
            // hide; Navigation.js still removes this item per-session for any
            // workshop that has not turned the flag on (same live pattern as
            // the Imaging/LMS/watsonx items here).
            {
                id: 'heidi-training',
                title: 'Heidi Model Training',
                type: 'item',
                icon: 'model_training',
                auth: [...authRoles.user, ...authRoles.instructor],
                training_allow: true,
                url: '/training-jobs',
                exact: false
            },
            {
                // Inside Heidi: lakehouse browser + read-only SQL (WatsonxData.js).
                id: 'watsonx-data',
                title: 'watsonx.data',
                subtitle: 'Data lakehouse',
                type: 'item',
                icon: 'storage',
                url: '/watsonx/data',
                exact: true
            },
            {
                // Inside Heidi: catalog + playground (WatsonxModels.js), with an
                // "Open in IBM Cloud" hand-off for the full tool.
                id: 'watsonx-models',
                title: 'watsonx.ai',
                subtitle: 'Models',
                type: 'item',
                icon: 'memory',
                url: '/watsonx/models',
                exact: true
            },
            {
                // Inside Heidi: agents + agent chat (WatsonxWorkflows.js).
                id: 'watsonx-workflows',
                title: 'watsonx Orchestrate',
                subtitle: 'Workflows',
                type: 'item',
                icon: 'device_hub',
                url: '/watsonx/workflows',
                exact: true
            }
        ]
    },
    // The Education vertical. Everything above it is vertical-agnostic; a
    // second vertical (pharma, finance...) gets its own section here rather
    // than being mixed into the ones above.
    {
        id: 'education',
        title: 'Education',
        type: 'group',
        icon: 'school',
        children: [
            {
                id: "trainings",
                title: "Heidi Workshops",
                type: "item",
                icon: "class",
                auth: authRoles.instructor,
                url: "/trainings",
                exact: true
            },
            {
                // Built from participants connecting to a workshop, so it sits
                // right under Heidi Workshops.
                id: "student-activity",
                title: "Student Activity",
                type: "item",
                icon: "equalizer",
                auth: authRoles.instructor,
                url: "/student-activity",
                exact: true
            },
            {
                id: 'courses',
                title: 'Courses',
                type: 'item',
                icon: 'school',
                training_allow: true,
                url: '/courses',
                exact: true
            },
            {
                id: "heidi-lms",
                title: "Heidi Campus",
                type: "collapse",
                // The bundled icon font is a fixed ligature set - menu_book,
                // auto_awesome and quiz are not in it and render as their own
                // name. Check public/assets/fonts/material-outline-icons
                // before using one.
                icon: "local_library",
                // Org admins AND instructors: most professors are instructors.
                // SSO is org-scoped, so it has no meaning for a platform
                // super-admin (and the hub refuses privileged SSO) - Navigation.js
                // removes the item for them and asks the portal per organization.
                auth: authRoles.instructor,
                url: "/heidi-lms",
                children: [
                    {
                        id: "heidi-lms-courses",
                        title: "Your courses",
                        type: "item",
                        icon: "library_books",
                        auth: authRoles.instructor,
                        url: "/heidi-lms/courses",
                        exact: true
                    },
                    {
                        id: "heidi-lms-build",
                        title: "Build a course",
                        type: "item",
                        icon: "note_add",
                        auth: authRoles.instructor,
                        url: "/heidi-lms/build",
                        exact: true
                    },
                    {
                        id: "heidi-lms-teaching",
                        title: "Teaching",
                        type: "item",
                        icon: "school",
                        auth: authRoles.instructor,
                        url: "/heidi-lms/teaching",
                        exact: true
                    },
                    {
                        // Signs straight into the heidiai.io Advisor (/advising)
                        // through the same one-time SSO as Teaching.
                        id: "heidi-lms-advisor",
                        title: "Advisor",
                        type: "item",
                        icon: "supervisor_account",
                        auth: authRoles.instructor,
                        url: "/heidi-lms/advisor",
                        exact: true
                    }
                ]
            }
        ]
    },
    {
        id: 'cloud-tools',
        title: 'Cloud Tools',
        type: 'group',
        icon: 'cloud',
        auth: authRoles.superAdmin,
        children: [
            {
                id: 'cloud-analyzer',
                title: 'Heidi Cloud Analyzer',
                type: 'item',
                icon: 'cloud_circle',
                auth: authRoles.superAdmin,
                url: '/cloud-analyzer',
                exact: true
            },
            {
                id: 'cost-reports',
                title: 'Cost Reports',
                type: 'item',
                icon: 'assessment',
                auth: authRoles.superAdmin,
                url: '/cost-reports',
                exact: true
            }
        ]
    }
];

export default navigationConfig;

